/**
 * consoleNoiseFilter -- b18-telemetry-noise-filter
 *
 * Investigation summary
 * ---------------------
 * Build 219 telemetry paste contained 11,301 lines; 2,500 (22%) were
 * `WebProcess::markAllLayersVolatile: Failed to mark layers as volatile`
 * emitted by iOS WebKit when WKWebView enters the background. These
 * messages originate from the iOS unified logging system at the
 * os_log level inside Apple's WebContent process. They are NOT
 * Nexus-emitted lines.
 *
 * Mechanism
 * ---------
 * Capacitor 8 patches window.console on iOS via native-bridge.js so
 * that every JS console.log/warn/error/etc. call is forwarded to the
 * native CAPConsolePlugin, which then writes via CAPLog.print() to
 * Xcode's device console stream. Nexus log lines ([live-scan],
 * [dex-scan], etc.) travel this path and appear alongside the system
 * os_log output in the same Xcode console pane.
 *
 * The markAllLayersVolatile messages themselves originate at the OS
 * level and do NOT pass through the Capacitor console bridge. They
 * cannot be suppressed from inside the WebView. However, the OS log
 * stream and the Capacitor JS->native log stream are interleaved in
 * Xcode/Console.app, making telemetry pastes unreadable.
 *
 * What this filter does
 * ---------------------
 * Installs a thin wrapper around window.console methods (log, warn,
 * info, error, debug) on iOS. Any message whose stringified form
 * matches a NOISE_PATTERNS entry is silently dropped so it:
 *   - Does not reach CAPConsolePlugin (stopping the Xcode CAPLog echo)
 *   - Does not appear in any browser devtools session
 *
 * This covers the case where a future Capacitor version, a WKWebView
 * configuration change, or a custom plugin starts forwarding OS-level
 * log lines back into JS context.
 *
 * What this filter explicitly does NOT suppress
 * ---------------------------------------------
 * - Any line containing [live-scan], [dex-scan], [NexPip3D],
 *   [useDexLiveScan], or any other Nexus-emitted prefix
 * - Real iOS errors: WEBP/JPEG decode failures, network 5xx, broadcast
 *   errors, Vision OCR errors
 * - Any line not matching the explicit NOISE_PATTERNS list
 *
 * Adding new patterns
 * -------------------
 * Append a new RegExp to NOISE_PATTERNS. Patterns are matched against
 * the full stringified message (all arguments joined with a single
 * space). Keep each pattern as specific as possible to avoid
 * accidental suppression of real signal.
 */

import { Capacitor } from "@capacitor/core";

/**
 * iOS WebKit / WebProcess system-level noise patterns.
 *
 * Each pattern is matched against the full joined message string.
 * Only iOS system-emitted messages belong here -- never Nexus prefixes.
 */
const NOISE_PATTERNS: RegExp[] = [
  // iOS WebKit: fires ~every 4 s when the app is backgrounded.
  // WebContent process tries and fails to mark GPU layers volatile.
  // Harmless; no action needed. The source is Apple WebKit, not Nexus.
  /WebProcess::markAllLayersVolatile/,

  // iOS WebKit: companion message when layer volatility marking fails.
  /Failed to mark layers as volatile/,

  // WebProcessProxy: WKWebView unresponsive warning that fires alongside
  // the markAllLayersVolatile burst during backgrounding. Not an error
  // actionable by the app.
  /WebProcessProxy::didBecomeUnresponsive/,
];

/**
 * Nexus-emitted prefixes that must NEVER be suppressed regardless of
 * any pattern match. This is a safety net: if a future NOISE_PATTERNS
 * entry is accidentally too broad, any line that contains a known
 * Nexus prefix is passed through unconditionally.
 */
const NEXUS_PREFIXES: string[] = [
  "[live-scan]",
  "[dex-scan]",
  "[NexPip3D]",
  "[useDexLiveScan]",
  "[broadcast]",
  "[BroadcastBridge]",
  "[splash]",
  "[SessionProvider]",
];

type ConsoleMethod = "log" | "warn" | "info" | "error" | "debug";

const PATCHED_METHODS: ConsoleMethod[] = ["log", "warn", "info", "error", "debug"];

/**
 * Serialize a single console argument to a string for pattern matching.
 * Mirrors the light serialization Capacitor itself applies before
 * forwarding to CAPConsolePlugin.
 */
function serializeArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg === null) return "null";
  if (arg === undefined) return "undefined";
  try {
    return String(arg);
  } catch {
    return "[unserializable]";
  }
}

/**
 * Install the noise filter. Safe to call multiple times; subsequent
 * calls after the first are no-ops (idempotent via __nexusNoiseFilterInstalled
 * sentinel on window.console).
 *
 * Installs only on iOS native (where the Capacitor console bridge runs).
 * On web and Android the filter is a no-op so development workflows are
 * unaffected.
 */
export function installConsoleNoiseFilter(): void {
  // Only install on iOS native builds where the Capacitor console
  // bridge is active. On web the filter is not needed and would add
  // noise to devtools.
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
    return;
  }

  // Idempotent: don't double-wrap if called more than once.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((console as any).__nexusNoiseFilterInstalled) {
    return;
  }

  for (const method of PATCHED_METHODS) {
    const original = console[method].bind(console);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (console as any)[method] = (...args: unknown[]) => {
      const msg = args.map(serializeArg).join(" ");

      // Safety net: never suppress a line with a known Nexus prefix.
      const isNexusLine = NEXUS_PREFIXES.some((prefix) => msg.includes(prefix));
      if (isNexusLine) {
        original(...args);
        return;
      }

      // Drop if the message matches any noise pattern.
      if (NOISE_PATTERNS.some((pattern) => pattern.test(msg))) {
        return;
      }

      original(...args);
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (console as any).__nexusNoiseFilterInstalled = true;
}
